import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import TextDeleteTagModal from 'tags/components/TextDeleteTagModal.component';
import TextEditWarningModal from 'tags/components/TextEditWarningModal.component';
import CustomClientTag from 'tags/CustomClientTag.component';

import deleteLocalSalonTag from 'actions/tags/deleteLocalSalonTag.action';
import deleteSalonTag from 'actions/tags/deleteSalonTag.action';
import { DELETE_SALON_TAG_FAILURE, DELETE_SALON_TAG_SUCCESS } from 'actions/tags/deleteSalonTag.types';
import saveSalonTag from 'actions/tags/saveSalonTag.action';
import { SAVE_SALON_TAG_FAILURE, SAVE_SALON_TAG_SUCCESS } from 'actions/tags/saveSalonTag.types';
import ConfirmationDialog from 'components/dialog/ConfirmationDialog.component';
import { useTranslation } from 'providers/GILT.provider';
import { selectCurrentSalonCalendarMode, selectCurrentSalonId } from 'selectors/currentSalon.selectors';
import fromAnyToString from 'utils/converters/fromAnyToString';
import logger from 'utils/logger.util';
import isTempTag from 'utils/predicates/isTempTag.util';

const L = logger('CustomClientTagContainer');

const ALERTS = Object.freeze({
  success: 'success',
  warning: 'warning',
  danger: 'danger',
});

const CustomClientTagContainer = ({
  calendarMode,
  deleteLocalSalonTag,
  deleteSalonTag,
  description,
  emoticon,
  id,
  name,
  numberOfClients,
  salonId,
  saveSalonTag,
  setUnsavedChanges,
  showAlert,
  validateName,
}) => {
  const { t } = useTranslation();
  const [editTagModal, setEditTagModal] = useState(false);
  const [deleteTagModal, setDeleteTagModal] = useState(false);
  const [isTagSaved, setIsTagSaved] = useState(true);
  const [savedTag, setSavedTag] = useState({});
  const [tagId, setTagId] = useState(null);
  const [selectedNumberOfClients, setSelectedNumberOfClients] = useState(0);

  const ALERT_MESSAGE = Object.freeze({
    save: t('clientTags.alerts.saveSuccess'),
    saveFail: t('clientTags.alerts.saveFail'),
    delete: t('clientTags.alerts.deleteSuccess'),
    deleteFail: t('clientTags.alerts.deleteFail'),
    error: t('clientTags.alerts.error'),
  });

  const onSaveTag = useCallback(
    async ({ savedTag }) => {
      L.unignore(
        'onSaveTag()',
        'saveSalonTag:',
        await saveSalonTag({ salonId, savedTag })
          .then((res) => {
            const { type } = res;

            if (type && type === SAVE_SALON_TAG_SUCCESS) {
              setIsTagSaved(true);
              setEditTagModal(false);
              setUnsavedChanges(false);
              showAlert(ALERTS.success, ALERT_MESSAGE.save);
            }

            if (type && type === SAVE_SALON_TAG_FAILURE) {
              setIsTagSaved(false);
              setEditTagModal(false);
              setUnsavedChanges(true);
              showAlert(ALERTS.danger, ALERT_MESSAGE.saveFail);
            }
          })
          .catch((error) => {
            L.warn('SaveTag()', 'saveSalonTag()', 'catch()', { error });
            setEditTagModal(false);
            showAlert(ALERTS.danger, ALERT_MESSAGE.error);
          }),
      );
    },
    [ALERT_MESSAGE.error, ALERT_MESSAGE.save, ALERT_MESSAGE.saveFail, salonId, saveSalonTag, setUnsavedChanges, showAlert],
  );

  const handleSaveTag = useCallback(
    ({ savedTag }) => {
      setEditTagModal(numberOfClients !== 0);
      setSelectedNumberOfClients(savedTag.numberOfClients);
      setSavedTag(savedTag);
      setIsTagSaved(false);
      if (numberOfClients === 0) onSaveTag({ savedTag });
    },
    [numberOfClients, onSaveTag],
  );

  const deleteTag = useCallback(
    async ({ tagId }) => {
      if (isTempTag(fromAnyToString(tagId))) {
        L.unignore(
          'deleteTag()',
          'deleteLocalSalonTag:',
          await deleteLocalSalonTag({ tagId })
            .then(() => {
              setDeleteTagModal(false);
              showAlert(ALERTS.success, ALERT_MESSAGE.delete);
            })
            .catch((error) => {
              L.warn('deleteTag()', 'deleteLocalSalonTag()', 'catch()', { error });
              setDeleteTagModal(false);
              showAlert(ALERTS.danger, ALERT_MESSAGE.error);
            }),
        );
        return;
      }

      L.unignore(
        'deleteTag()',
        'deleteSalonTag:',
        await deleteSalonTag({ salonId, tagId })
          .then((res) => {
            const { type } = res;

            if (type && type === DELETE_SALON_TAG_SUCCESS) {
              setDeleteTagModal(false);
              showAlert(ALERTS.success, ALERT_MESSAGE.delete);
            }

            if (type && type === DELETE_SALON_TAG_FAILURE) {
              setDeleteTagModal(false);
              showAlert(ALERTS.danger, ALERT_MESSAGE.deleteFail);
            }
          })
          .catch((error) => {
            L.warn('DeleteTag()', 'deleteSalonTag()', 'catch()', { error });
            setDeleteTagModal(false);
            showAlert(ALERTS.danger, ALERT_MESSAGE.error);
          }),
      );
    },
    [ALERT_MESSAGE.delete, ALERT_MESSAGE.deleteFail, ALERT_MESSAGE.error, deleteLocalSalonTag, deleteSalonTag, salonId, showAlert],
  );

  const handleDeleteTag = useCallback(
    ({ id, numberOfClients }) => {
      setDeleteTagModal(numberOfClients !== 0);
      setSelectedNumberOfClients(numberOfClients);
      setTagId(id);
      return numberOfClients === 0 ? deleteTag({ tagId: id }) : null;
    },
    [deleteTag],
  );
  const onClose = useCallback(() => {
    setDeleteTagModal(false);
    setEditTagModal(false);
  }, []);

  const warningTitle = t('clientTags.warningTitle', { numberOfClients: selectedNumberOfClients });

  return (
    <div data-bem="CustomClientTagContainer">
      <CustomClientTag
        calendarMode={calendarMode}
        deleteTag={handleDeleteTag}
        description={description}
        emoticon={emoticon}
        id={id}
        isTagSaved={isTagSaved}
        name={name}
        numberOfClients={numberOfClients}
        salonId={salonId}
        saveTag={handleSaveTag}
        setIsTagSaved={setIsTagSaved}
        setUnsavedChanges={setUnsavedChanges}
        validateName={validateName}
      />
      {editTagModal && (
        <ConfirmationDialog
          isOpen={editTagModal}
          leftButtonText={t('clientTags.editTagModal.cancelButton')}
          onLeft={onClose}
          onRight={() => onSaveTag({ savedTag })}
          rightButtonText={t('clientTags.editTagModal.confirmButton')}
          title={warningTitle}
        >
          <TextEditWarningModal />
        </ConfirmationDialog>
      )}

      {deleteTagModal && (
        <ConfirmationDialog
          isOpen={deleteTagModal}
          leftButtonText={t('clientTags.confirmationModal.backButton')}
          onLeft={onClose}
          onRight={() => deleteTag({ tagId })}
          rightButtonText={t('clientTags.confirmationModal.deleteButton')}
          title={warningTitle}
        >
          <TextDeleteTagModal />
        </ConfirmationDialog>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  calendarMode: selectCurrentSalonCalendarMode(state),
  salonId: selectCurrentSalonId(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      deleteLocalSalonTag,
      deleteSalonTag,
      saveSalonTag,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(CustomClientTagContainer);
